/**
 * The tape format.
 *
 * A tape is an *initial condition plus a stick*, and nothing else. There is no
 * recorded trajectory in it: playback re-runs the identical simulation and the
 * aeroplane goes where it went because the physics put it there. That is the
 * same call the original made — `RBHEX4.TXT` records that editing the `.DAT`
 * performance tables makes shipped `.VCR` tapes play back wrong, "an aircraft
 * will shoot at a target that's not there", which is only possible if the tape
 * stores inputs and the game re-simulates them.
 *
 * It is also why the tape carries the whole `MissionSpec` rather than the
 * catalog id and seed it was generated from. A spec is already plain JSON by
 * design (`mission/mission-spec.ts`), a generator is not — regenerating from
 * `(id, seed)` would silently produce a different sky the first time a
 * generator is touched. The original did exactly this too: `TAPES/LOOP.VCR` at
 * offset `0x44` is byte-identical to a `.CUS` mission definition
 * (docs/formats/missions-campaign.md §.VCR), so a tape has always been
 * self-contained enough to post to a friend.
 *
 * ## Layout
 *
 * JSON, not a binary codec, for the reason `net/protocol.ts` gives for the duel
 * wire: the payload is already integers, packing them saves little, and a tape
 * that can be read in a text editor is worth much more than that the first time
 * a replay diverges and somebody has to work out why. The one place density
 * mattered — the per-tick stick — is a tuple array rather than objects
 * (`input-trace.ts`).
 *
 * ```jsonc
 * {
 *   "format": "red-opus/tape",
 *   "version": 1,                  // TAPE_FORMAT_VERSION — this envelope's shape
 *   "name": "SPLIT_S",             // <= 8 characters, the original's own limit
 *   "title": "Balloon Busting",    // what the mission called itself
 *   "recordedAt": "2026-08-21T…",
 *   "guard": { "snapshotVersion": 4, "streams": "gunJam,…", "engine": "621df293…" },
 *   "playerId": "player",
 *   "ticks": 12345,
 *   "subject": { "kind": "mission", "mission": { … } },
 *   "trace": [[90, 0, 0, 0, 1000, 0], …],
 *   "checkpoints": [[300, "a1b2…"], [600, "c3d4…"], …]
 * }
 * ```
 *
 * ## How the embedded mission is validated
 *
 * The *envelope* is read field by field and refused with the field named, the
 * way `flight/snapshot.ts` reads a snapshot. The embedded subject is not, and
 * deliberately — it is **content-addressed** instead: the tape carries
 * `subjectHash`, the canonical-JSON fingerprint of the spec as recorded, and
 * loading re-computes it and refuses a mismatch.
 *
 * That is not a shortcut around writing a validator, it is a stronger check
 * than one. A field-by-field reader for a structure with flights, targets,
 * flak, objectives, waypoints and a raid plan proves the file is *a* valid
 * spec; the hash proves it is *the* spec, every field of it, including fields
 * this build has never heard of. It also cannot rot: a lane that adds a member
 * to `MissionSpec` gets the new member covered for free, where a hand-written
 * reader would have to be remembered and would silently drop it otherwise —
 * exactly the failure mode `flight/snapshot.ts`'s version history is a record
 * of.
 */

import type { EncounterSetup } from "../combat/encounter.ts";
import type { MissionSpec } from "../mission/mission-spec.ts";
import { canonicalJson, contentHash } from "../flight/hash.ts";
import { RNG_STREAM_NAMES } from "../flight/rng.ts";
import { SNAPSHOT_VERSION } from "../flight/snapshot.ts";
import { readMember, readNumber, readObject, requireObject, type PlainObject } from "../flight/plain.ts";
import { engineFingerprint } from "./engine-fingerprint.ts";
import { traceTicks, type InputRun } from "./input-trace.ts";

/** Magic string, so a stray JSON file is refused before anything else is read. */
export const TAPE_FORMAT = "red-opus/tape";

/** The *envelope's* version. Bumped only when this file's own layout changes; the behaviour guard is `TapeGuard`. */
export const TAPE_FORMAT_VERSION = 1;

/**
 * The original's limit, kept on purpose: "IMPORTANT! The mission name that you
 * choose can be no longer than 8 characters long" (manual, THE MISSION
 * RECORDER). Nothing in this port needs the restriction — it is here because
 * the tape list reads like the original's `SELECT TAPE TO VIEW:` when the names
 * are the same shape as the original's own `SPLIT_S`, `IMMEL`, `HALFLOOP`.
 */
export const TAPE_NAME_MAX = 8;

/** `[tick, hash]` — an encounter fingerprint at one tick of the recording. */
export type TapeCheckpoint = readonly [number, string];

export const TAPE_SUBJECT_KINDS = ["mission", "encounter"] as const;

/**
 * What was flown. A mission carries objectives and a debrief; a bare encounter
 * is the instant dogfight, which has neither and is still worth keeping.
 */
export type TapeSubject =
  | { readonly kind: "mission"; readonly mission: MissionSpec }
  | { readonly kind: "encounter"; readonly encounter: EncounterSetup };

/** Everything that decides whether this build can replay this tape faithfully. */
export interface TapeGuard {
  readonly snapshotVersion: number;
  /** `RNG_STREAM_NAMES` as recorded. A stream added or renamed moves every seed. */
  readonly streams: string;
  /** `engineFingerprint()` as recorded — the behavioural half. */
  readonly engine: string;
}

export interface ReplayTape {
  readonly format: typeof TAPE_FORMAT;
  readonly version: number;
  readonly name: string;
  readonly title: string;
  /** ISO-8601, for ordering the tape list. Never read by the simulation. */
  readonly recordedAt: string;
  readonly guard: TapeGuard;
  /** Which aircraft id in the subject the trace is the stick for. */
  readonly playerId: string;
  readonly ticks: number;
  readonly subject: TapeSubject;
  /** `subjectHash(subject)` as sealed — see the module doc on content-addressing. */
  readonly subjectHash: string;
  readonly trace: readonly InputRun[];
  readonly checkpoints: readonly TapeCheckpoint[];
}

/** This build's own guard — what a tape must match to be replayable here. */
export function currentGuard(): TapeGuard {
  return {
    snapshotVersion: SNAPSHOT_VERSION,
    streams: RNG_STREAM_NAMES.join(","),
    engine: engineFingerprint(),
  };
}

/**
 * Trims a proposed tape name to the original's eight characters, the way the
 * original did: "If you type a name longer than 8 characters, the computer will
 * cut off the additional characters, saving only the first 8." Upper-cased and
 * stripped of anything a DOS filename could not hold, so the tape list reads
 * like the original's.
 */
export function tapeName(proposed: string): string {
  const cleaned = proposed.toUpperCase().replace(/[^A-Z0-9_]/g, "").slice(0, TAPE_NAME_MAX);
  return cleaned.length > 0 ? cleaned : "TAPE";
}

export interface GuardVerdict {
  readonly ok: boolean;
  /**
   * One sentence per mismatch, each naming what the tape wants and what this
   * build is. Empty when `ok`. The VCR shows these verbatim: a tape that cannot
   * be played should say why in words a person can act on, not "incompatible".
   */
  readonly reasons: readonly string[];
}

/**
 * Whether this build can replay `tape` faithfully.
 *
 * A mismatch is never repaired and never overridden. A tape recorded against a
 * different flight model does not replay "approximately" — it replays a
 * different flight that happens to start in the same place, which is worse than
 * refusing because it looks like it worked. This is the check that is *meant*
 * to fire when the flight-model or wind lanes land; see `replay.md` on
 * re-recording the pinned fixture.
 */
export function checkGuard(tape: ReplayTape, current: TapeGuard = currentGuard()): GuardVerdict {
  const reasons: string[] = [];
  if (tape.version !== TAPE_FORMAT_VERSION) {
    reasons.push(`tape format version ${tape.version}; this build reads version ${TAPE_FORMAT_VERSION}`);
  }
  if (tape.guard.snapshotVersion !== current.snapshotVersion) {
    reasons.push(`recorded on snapshot version ${tape.guard.snapshotVersion}; this build is version ${current.snapshotVersion}`);
  }
  if (tape.guard.streams !== current.streams) {
    reasons.push(`recorded with random streams [${tape.guard.streams}]; this build has [${current.streams}]`);
  }
  if (tape.guard.engine !== current.engine) {
    reasons.push(`recorded against flight model ${tape.guard.engine}; this build is ${current.engine}`);
  }
  return { ok: reasons.length === 0, reasons };
}

function readString(source: PlainObject, key: string): string {
  const value = source[key];
  if (typeof value !== "string") throw new TypeError(`${key}: expected a string, got ${typeof value}`);
  return value;
}

function readGuard(source: PlainObject): TapeGuard {
  const guard = readObject(source, "guard");
  return {
    snapshotVersion: readNumber(guard, "snapshotVersion"),
    streams: readString(guard, "streams"),
    engine: readString(guard, "engine"),
  };
}

function readRun(value: unknown, index: number): InputRun {
  if (!Array.isArray(value) || value.length !== 6) {
    throw new TypeError(`trace[${index}]: expected six integers, got ${JSON.stringify(value)}`);
  }
  const run = value.map((entry, slot) => {
    if (typeof entry !== "number" || !Number.isInteger(entry)) {
      throw new TypeError(`trace[${index}][${slot}]: expected an integer, got ${String(entry)}`);
    }
    return entry;
  });
  const [ticks, pitch, roll, yaw, throttle, flags] = run as [number, number, number, number, number, number];
  if (ticks <= 0) throw new RangeError(`trace[${index}]: a run must cover at least one tick, got ${ticks}`);
  return [ticks, pitch, roll, yaw, throttle, flags];
}

function readTrace(source: PlainObject): InputRun[] {
  const value = source.trace;
  if (!Array.isArray(value)) throw new TypeError(`trace: expected an array, got ${typeof value}`);
  return value.map(readRun);
}

function readCheckpoints(source: PlainObject): TapeCheckpoint[] {
  const value = source.checkpoints;
  if (!Array.isArray(value)) throw new TypeError(`checkpoints: expected an array, got ${typeof value}`);
  return value.map((entry, index) => {
    if (!Array.isArray(entry) || entry.length !== 2) {
      throw new TypeError(`checkpoints[${index}]: expected [tick, hash]`);
    }
    const [tick, hash] = entry as [unknown, unknown];
    if (typeof tick !== "number" || !Number.isInteger(tick) || tick < 0) {
      throw new TypeError(`checkpoints[${index}][0]: expected a tick, got ${String(tick)}`);
    }
    if (typeof hash !== "string" || hash.length === 0) {
      throw new TypeError(`checkpoints[${index}][1]: expected a hash, got ${String(hash)}`);
    }
    return [tick, hash] as TapeCheckpoint;
  });
}

/**
 * The one place a tape's JSON becomes a typed value.
 *
 * The narrowing is earned rather than assumed: every caller has already proved
 * that this object's canonical JSON hashes to the fingerprint the tape was
 * sealed with, so the claim being made is not "this file looks like a
 * `MissionSpec`" but "this is byte-for-byte the object that was recorded".
 */
function asRecorded<T>(verified: unknown): T {
  return verified as T;
}

/** Canonical fingerprint of a subject — what `subjectHash` holds and what loading re-computes. */
export function subjectHash(subject: TapeSubject): string {
  return contentHash(canonicalJson(subject));
}

function readSubject(source: PlainObject): TapeSubject {
  const raw = readObject(source, "subject");
  const kind = readMember(raw, "kind", TAPE_SUBJECT_KINDS);
  const subject: TapeSubject =
    kind === "mission"
      ? { kind, mission: asRecorded<MissionSpec>(readObject(raw, "mission")) }
      : { kind, encounter: asRecorded<EncounterSetup>(readObject(raw, "encounter")) };
  const expected = readString(source, "subjectHash");
  const found = subjectHash(subject);
  if (found !== expected) {
    throw new RangeError(`subject: content hash ${found} does not match the tape's own ${expected}`);
  }
  return subject;
}

/**
 * Reads a tape from parsed JSON, refusing anything malformed with the field
 * named. Does *not* check the guard — a tape this build cannot replay is still
 * a tape worth listing, naming and exporting, so refusing to parse it would
 * take away the one thing a stale tape is still good for.
 */
export function readTape(value: unknown): ReplayTape {
  const root = requireObject(value, "tape");
  const format = readString(root, "format");
  if (format !== TAPE_FORMAT) throw new TypeError(`format: expected "${TAPE_FORMAT}", got "${format}"`);
  const trace = readTrace(root);
  const ticks = readNumber(root, "ticks");
  const counted = traceTicks(trace);
  if (counted !== ticks) throw new RangeError(`ticks: tape says ${ticks}, its trace covers ${counted}`);
  const subject = readSubject(root);
  return {
    format: TAPE_FORMAT,
    version: readNumber(root, "version"),
    name: readString(root, "name"),
    title: readString(root, "title"),
    recordedAt: readString(root, "recordedAt"),
    guard: readGuard(root),
    playerId: readString(root, "playerId"),
    ticks,
    subject,
    subjectHash: subjectHash(subject),
    trace,
    checkpoints: readCheckpoints(root),
  };
}

export function parseTape(text: string): ReplayTape {
  return readTape(JSON.parse(text));
}

/** A tape as a file, ready for `localStorage` or a download. */
export function serializeTape(tape: ReplayTape): string {
  return JSON.stringify(tape);
}
